import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { UiHelperService } from '../../services/ui-helper.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly uiHelper = inject(UiHelperService);

  /**
   * Intercepta todas las peticiones HTTP y maneja errores de forma centralizada.
   * Prioriza el texto del backend (`mensaje` / `message` / `descripcionErrores`);
   * el genérico por status solo aplica si el body no trae mensaje usable.
   * No reintenta automáticamente: un retry en POST create RSE duplicaría procesos.
   * En cualquier error HTTP desbloquea overlay de carga (safety-net global).
   */
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        this.releaseUiOnHttpError();
        if (error.error instanceof Blob) {
          return from(error.error.text()).pipe(
            switchMap((text) =>
              throwError(() => this.toStructuredError(req, error, this.messageFromBodyText(text)))
            ),
            catchError(() =>
              throwError(() => this.toStructuredError(req, error, null))
            )
          );
        }
        return throwError(() =>
          this.toStructuredError(req, error, this.extractBackendMessage(error))
        );
      })
    );
  }

  /** Evita pantalla bloqueada si algún subscribe olvidó hideSpinner en el error. */
  private releaseUiOnHttpError(): void {
    try {
      this.uiHelper.unlockUi();
    } catch {
      // ignore
    }
  }

  private toStructuredError(
    req: HttpRequest<any>,
    error: HttpErrorResponse,
    backendMessage: string | null
  ): {
    message: string;
    code: string;
    status: number;
    url: string | null;
    timestamp: string;
    originalError: HttpErrorResponse;
  } {
    let errorMessage = 'Ha ocurrido un error inesperado';
    let errorCode = 'UNKNOWN_ERROR';

    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error de red: ${error.error.message}`;
      errorCode = 'CLIENT_ERROR';
    } else {
      errorCode = `HTTP_${error.status}`;
      switch (error.status) {
        case 0:
          errorMessage = 'No se pudo conectar al servidor. Verifica tu conexión a internet.';
          errorCode = 'NO_CONNECTION';
          break;
        case 400:
          errorMessage = backendMessage || 'Solicitud inválida. Verifica los datos enviados.';
          break;
        case 401:
          errorMessage = backendMessage || 'No autorizado. Verifica tus credenciales.';
          break;
        case 403:
          errorMessage = backendMessage || 'Acceso prohibido. No tienes permisos para esta operación.';
          break;
        case 404:
          errorMessage = backendMessage || 'Recurso no encontrado. Verifica la URL.';
          break;
        case 408:
          errorMessage = backendMessage || 'La solicitud tardó demasiado tiempo. Intenta nuevamente.';
          break;
        case 409:
          errorMessage = backendMessage || 'Conflicto con el estado actual del recurso.';
          break;
        case 413:
          errorMessage = backendMessage || 'El archivo es demasiado grande. Máximo 150MB por archivo.';
          break;
        case 415:
          errorMessage = backendMessage || 'Tipo de archivo no soportado.';
          break;
        case 422:
          errorMessage = backendMessage || 'Los datos enviados no son válidos.';
          break;
        case 429:
          errorMessage = backendMessage || 'Demasiadas solicitudes. Espera un momento e intenta nuevamente.';
          break;
        case 500:
          errorMessage = backendMessage || 'Error interno del servidor. Intenta nuevamente más tarde.';
          break;
        case 502:
          errorMessage = backendMessage || 'Error de puerta de enlace. El servidor no está disponible.';
          break;
        case 503:
          errorMessage = backendMessage || 'Servicio no disponible temporalmente. Intenta más tarde.';
          break;
        case 504:
          errorMessage = backendMessage || 'Tiempo de espera agotado. El servidor no responde.';
          break;
        default:
          errorMessage = backendMessage || error.message || `Error del servidor: ${error.status}`;
      }

      console.error('🔴 Error HTTP:', {
        status: error.status,
        statusText: error.statusText,
        message: errorMessage,
        url: error.url,
        method: req.method,
        errorCode,
        timestamp: new Date().toISOString(),
        errorBody: error.error
      });
    }

    return {
      message: errorMessage,
      code: errorCode,
      status: error.status,
      url: error.url,
      timestamp: new Date().toISOString(),
      originalError: error
    };
  }

  private extractBackendMessage(error: HttpErrorResponse): string | null {
    const body = error.error;
    if (!body || body instanceof Blob || body instanceof ArrayBuffer) {
      return null;
    }
    if (typeof body === 'string') {
      return this.messageFromBodyText(body);
    }
    return this.messageFromBodyObject(body as Record<string, unknown>);
  }

  private messageFromBodyText(text: string): string | null {
    if (!text?.trim()) {
      return null;
    }
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      return this.messageFromBodyObject(parsed) || text.trim();
    } catch {
      return text.trim();
    }
  }

  /** Contratos: Sign_RSE/BFF (`message`) y postventa/gestión (`mensaje`, `descripcionErrores`). */
  private messageFromBodyObject(body: Record<string, unknown> | null | undefined): string | null {
    if (!body || typeof body !== 'object') {
      return null;
    }
    const mensaje = body['mensaje'];
    if (typeof mensaje === 'string' && mensaje.trim()) {
      return mensaje.trim();
    }
    const message = body['message'];
    if (typeof message === 'string' && message.trim()) {
      return message.trim();
    }
    const descripcion = body['descripcionErrores'];
    if (Array.isArray(descripcion)) {
      const parts = descripcion
        .map((item) => (typeof item === 'string' ? item.trim() : ''))
        .filter(Boolean);
      if (parts.length) {
        return parts.join('\n');
      }
    }
    return null;
  }
}
